import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent } from '../ui/ResponsiveLayout';
import BrandSymbol from '../ui/BrandSymbol';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.endofanera.ai';
const RESEND_COOLDOWN = 60; // seconds

const VerifyEmailPending: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const email = searchParams.get('email') || sessionStorage.getItem('pending_verification_email') || '';
  
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Clear session storage on mount
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('pending_verification_email');
    };
  }, []);

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending || !email) return;

    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setResendSuccess(true);
        setResendCooldown(RESEND_COOLDOWN);
      } else {
        setError(data.message || 'Failed to resend verification email');
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      setError('Failed to resend. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const maskedEmail = email ? 
    email.replace(/(.{2})(.*)(@.*)/, '$1***$3') : 
    'your email';

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <div className="max-w-xl mx-auto py-12">
          {/* Header */}
          <div className="flex flex-col items-center justify-center w-full">
            <BrandSymbol size="4xl" variant="dark" showText={true} className="mb-6 mx-auto" />
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
            {/* Icon Header */}
            <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 px-8 py-8 text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-display font-semibold text-white">
                Check Your Email
              </h1>
            </div>

            {/* Content */}
            <div className="px-8 py-8">
              <p className="text-stone-700 text-center mb-6 leading-relaxed">
                We've sent a verification email to{' '}
                <span className="font-semibold text-stone-900">{maskedEmail}</span>.
                Please click the link in the email to verify your account.
              </p>

              {/* Security Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm text-amber-800 font-medium mb-1">
                      Why is this important?
                    </p>
                    <p className="text-sm text-amber-700">
                      Email verification helps protect your account and ensures only you can securely access the information and plans connected to it.
                    </p>
                  </div>
                </div>
              </div>

              {/* Resend Section */}
              <div className="text-center">
                <p className="text-sm text-stone-500 mb-3">
                  Didn't receive the email? Check your spam folder or
                </p>
                
                {resendSuccess && (
                  <div className="bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg mb-3">
                    ✓ Verification email sent! Please check your inbox.
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 text-red-700 text-sm px-4 py-2 rounded-lg mb-3">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || isResending || !email}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    resendCooldown > 0 || isResending || !email
                      ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                      : 'bg-cyan-700 text-white hover:bg-cyan-800'
                  }`}
                >
                  {isResending 
                    ? 'Sending...' 
                    : resendCooldown > 0 
                      ? `Resend in ${resendCooldown}s`
                      : 'Resend Verification Email'
                  }
                </button>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-stone-200"></div>
                <span className="text-xs text-stone-400">OR</span>
                <div className="flex-1 h-px bg-stone-200"></div>
              </div>

              {/* Login Link */}
              <div className="text-center">
                <p className="text-sm text-stone-600 mb-2">
                  Already verified?
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="text-cyan-700 hover:text-cyan-800 font-medium"
                >
                  Go to Login →
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-stone-50 px-8 py-4 border-t border-stone-200">
              <p className="text-xs text-stone-500 text-center">
                Having trouble? <a href="mailto:support@endofanera.ai" className="text-cyan-700 hover:underline">Contact Support</a>
              </p>
            </div>
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default VerifyEmailPending;

